import { Extension, Mark } from '@tiptap/core'
import Blockquote from '@tiptap/extension-blockquote'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import Highlight from '@tiptap/extension-highlight'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extensions'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import apiConfigs from 'app/configs/apiConfigs'
import { fileToBase64 } from 'libs/file'
import { useCallback, useMemo } from 'react'
import { TiptapVideoResizeExtention } from './TiptapVideoResize'
import Youtube from '@tiptap/extension-youtube'
import TiptapEnhancedLink from './TipTapEnhancedLink'
export const LineHeightSupportedTags = [
  'paragraph',
  'heading',
  'blockquote',
  'listItem',
  'codeBlock',
]
export const useVideoExtension = () => {
  return useMemo(() => TiptapVideoResizeExtention, [])
}

// Custom Hooks
export const useNoTranslateMark = () => {
  return useMemo(
    () =>
      Mark.create({
        name: 'noTranslate',
        parseHTML() {
          return [{ tag: 'span[translate="no"]' }]
        },
        renderHTML() {
          return [
            'span',
            { translate: 'no', class: 'bg-teal-lightest rounded notranslate' },
            0,
          ]
        },
      }),
    [],
  )
}

export const useFontSizeExtension = () => {
  return useMemo(
    () =>
      Extension.create({
        name: 'fontSizeStyle',
        addGlobalAttributes() {
          return [
            {
              types: ['textStyle'],
              attributes: {
                fontSize: {
                  default: null,
                  parseHTML: (element) => element.style.fontSize || null,
                  renderHTML: (attributes) => {
                    if (!attributes.fontSize) return {}
                    return {
                      style: `font-size: ${attributes.fontSize}`,
                    }
                  },
                },
              },
            },
          ]
        },
      }),
    [],
  )
}

export const useEditorProps = () => {
  return useMemo(
    () => ({
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[300px] p-4 break-all',
        spellcheck: 'true',
        translate: 'no',
      },
    }),
    [],
  )
}

const ResponsiveYoutube = Youtube.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      videoId: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div.tiptap-youtube-video',
        getAttrs: (element) => {
          const iframe = element.querySelector('iframe')
          if (!iframe) return false

          const src = iframe.getAttribute('src')
          const match = src.match(/(?:embed\/|v=|\.be\/)([^?&]+)/)
          const videoId = match ? match[1] : null

          return {
            videoId,
            src,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const videoId = HTMLAttributes.videoId
    const thumbnailUrl = videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : ''

    return [
      'div',
      {
        class: `tiptap-youtube-video tiptap-youtube-${videoId}`,
        style: `
        position: relative;
        width: 100%;
        padding-bottom: 56.25%;
        height: 0;
        overflow: hidden;
        background: black ${thumbnailUrl ? `url(${thumbnailUrl}) center/cover no-repeat` : ''}
      `,
      },
      [
        'div', // Play button - ONLY in email/rendered HTML
        {
          style: `
          margin: 150px auto 0;
          width: 0;
          height: 0;
          border-left: 30px solid white;
          border-top: 20px solid transparent;
          border-bottom: 20px solid transparent;
        `,
        },
      ],
      [
        'iframe',
        {
          src: HTMLAttributes.src,
          style:
            'position:absolute;top:0;left:0;width:100%;height:100%;border:0;',
          allow:
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
          allowfullscreen: 'true',
          referrerpolicy: 'strict-origin-when-cross-origin',
        },
      ],
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const videoId = node.attrs.videoId
      const src = node.attrs.src
      const thumbnailUrl = videoId
        ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
        : ''

      const container = document.createElement('div')
      container.className = `tiptap-youtube-video tiptap-youtube-${videoId}`
      container.style.cssText = `
        position: relative;
        width: 100%;
        padding-bottom: 56.25%;
        height: 0;
        overflow: hidden;
        background: black ${thumbnailUrl ? `url(${thumbnailUrl}) center/cover no-repeat` : ''};
      `

      const iframe = document.createElement('iframe')
      iframe.src = src
      iframe.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;border:0;'
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      )
      iframe.setAttribute('allowfullscreen', 'true')
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')

      const deleteBtn = document.createElement('button')
      deleteBtn.innerHTML = '×'
      deleteBtn.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        width: 32px;
        height: 32px;
        background: rgba(239, 68, 68, 0.9);
        color: white;
        border: none;
        border-radius: 50%;
        font-size: 24px;
        cursor: pointer;
        z-index: 1;
        display: none;
        line-height: 1;
        padding: 0;
      `

      container.addEventListener('mouseenter', () => {
        deleteBtn.style.display = 'block'
      })
      container.addEventListener('mouseleave', () => {
        deleteBtn.style.display = 'none'
      })

      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault()
        if (typeof getPos === 'function') {
          const pos = getPos()
          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run()
        }
      })

      container.appendChild(iframe)
      container.appendChild(deleteBtn)

      return {
        dom: container,
        contentDOM: null,
      }
    }
  },
})

export const useEditorExtensions = (
  NoTranslate,
  FontSizeStyle,
  VideoExtension,
) => {
  return useMemo(
    () => [
      StarterKit.configure({
        image: false,
        blockquote: false,
        horizontalRule: false,
        link: false,
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
          HTMLAttributes: {
            style: 'margin-top: 1.5rem; margin-bottom: 1rem;',
          },
        },
        paragraph: {
          HTMLAttributes: {
            style: 'margin-bottom: 1rem; white-space: pre-wrap;',
          },
        },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Color,
      Underline,
      Superscript,
      ResponsiveYoutube.configure({
        controls: true,
        allowFullscreen: true,
        nocookie: false,
      }),
      HorizontalRule.configure({
        HTMLAttributes: {
          style:
            'border: none; border-top: 2px solid #e5e7eb; margin: 2rem 0; width: 100%;',
        },
      }),
      Subscript,
      LineHeight.configure({
        types: LineHeightSupportedTags,
      }),
      Table.configure({
        resizable: true,
        handleWidth: 5,
        cellMinWidth: 50,
        maxColumns: 10,
        allowTableNodeSelection: true,
        HTMLAttributes: {
          style:
            'display: contents; border-collapse: collapse; margin: 10px 0; table-layout: fixed; border: 1px solid #C5C5C5; min-width: 200px; max-width: 100%; word-break: break-word;overflow-wrap: anywhere;',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          style:
            'border: 1px solid #C5C5C5; padding: 8px 12px; vertical-align: top; background: white;',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          style:
            'border: 1px solid #C5C5C5; padding: 8px 12px; vertical-align: top; background: #f8f9fa; font-weight: 600; text-align: left;',
        },
      }),
      ImageWithResize.configure({
        HTMLAttributes: {
          class: 'h-auto cursor-pointer tiptap-responsive-img',
        },
        allowBase64: true,
        inline: true,
        addCommands() {
          return {
            setImage:
              (options) =>
              ({ commands }) => {
                return commands.insertContent({
                  type: this.name,
                  attrs: options,
                })
              },
            setImageAlignment:
              (alignment) =>
              ({ commands }) => {
                return commands.updateAttributes(this.name, {
                  style:
                    'text-align: ${alignment}; display: block; margin: 0 auto;',
                })
              },
            setImageSize:
              ({ width, height }) =>
              ({ commands }) => {
                return commands.updateAttributes(this.name, { width, height })
              },
          }
        },
      }),
      TiptapEnhancedLink.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          style: `
          text-decoration: underline;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: normal;
        `,
        },
      }),

      Placeholder.configure({
        placeholder: 'Start typing...',
      }),
      NoTranslate,
      VideoExtension,
      TextStyle,
      FontSizeStyle,
      Blockquote.configure({
        HTMLAttributes: {
          class: 'custom-blockquote',
          style:
            'word-break: break-word; overflow-wrap: anywhere; border-left: 4px solid #d1d5db; padding: 0.75rem 1.25rem; background-color: #f8fafc; margin: 1.5rem 0; font-style: italic;',
        },
      }),
      FontFamily.configure({
        types: ['textStyle'],
      }),
    ],
    [NoTranslate, FontSizeStyle, VideoExtension, Blockquote],
  )
}

export const generateBodyImageId = () =>
  `body-img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

export const removeBodyImageFromEditor = (editor, bodyImageId) => {
  if (!editor || !bodyImageId) return false

  let found = false
  editor.commands.command(({ tr, state }) => {
    const { doc } = state
    doc.descendants((node, pos) => {
      if (
        node.type.name === 'image' &&
        node.attrs['data-body-image-id'] === bodyImageId
      ) {
        tr.delete(pos, pos + node.nodeSize)
        found = true
      }
    })
    return found
  })
  return found
}

export const useToolbarHandlers = (
  editor,
  onUploading,
  onUploaded,
  hancleImageChange,
  place,
  onEditorLoading,
  setEditorErrorMessage,
  formatMessage,
  accessToken,
  onBodyImageAdded,
) => {
  const exec = useCallback(
    (cmd, args = []) => {
      if (!editor) return

      if (cmd === 'toggleNoTranslate') {
        editor.chain().focus().toggleMark('noTranslate').run()
        return
      }

      const chain = editor.chain().focus()
      if (args.length) {
        chain[cmd](...args).run()
      } else {
        chain[cmd]().run()
      }
    },
    [editor],
  )

  const handleColorChange = useCallback(
    (e) => {
      const color = e.target.value
      editor?.chain().focus().setColor(color).run()
    },
    [editor],
  )

  const handleFontFamily = useCallback(
    (value) => {
      if (value === 'unset') {
        editor?.chain().focus().unsetFontFamily().run()
      } else {
        editor?.chain().focus().setFontFamily(value).run()
      }
    },
    [editor],
  )

  const handleFontSize = useCallback(
    (size) => {
      const chain = editor?.chain().focus()

      if (size === 'unset' || size === 'default') {
        chain?.unsetMark('textStyle').run()
      } else {
        chain?.setMark('textStyle', { fontSize: size }).run()
      }
    },
    [editor],
  )

  const uploadImage = useCallback(
    async (file) => {
      if (!file || !editor) return

      const MAX_FILE_SIZE = 25 * 1024 * 1024

      if (file.size > MAX_FILE_SIZE) {
        setEditorErrorMessage(
          formatMessage(
            { id: 'createNotification.media.maximumSize' },
            { limitSizeMB: 25 },
          ),
        )
        if (onUploaded) onUploaded()
        return
      }

      // Image type validation - only jpg, jpeg, png, webp allowed
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
      ]
      if (!allowedTypes.includes(file.type)) {
        setEditorErrorMessage(
          formatMessage({ id: 'error.400.fileTypeInvalid' }),
        )
        if (onUploaded) onUploaded()
        return
      }

      try {
        if (onUploading) onUploading()

        onEditorLoading(true)

        const baseUrl = apiConfigs.apiURL
        const uploadUrl = `${baseUrl}/places/${place.id}/upload`
        const base64 = await fileToBase64(file)

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ file: base64 }),
          credentials: 'include',
        })

        if (!response.ok) {
          onEditorLoading(false)
          setEditorErrorMessage(
            formatMessage({
              id: 'createNotification.media.multipleImage.upload.failed',
            }),
          )
          throw new Error('Upload failed')
        }

        const result = await response.json()
        const imagePath = result.url

        if (!imagePath) {
          setEditorErrorMessage('No path found in response:', result)
          onEditorLoading(false)
          if (onUploaded) onUploaded()
          return
        }

        const finalUrl = `${imagePath}?tr=w-1000,q-50`
        const cdnBaseUrl = apiConfigs.cndUrl
        const fileUrl = `${cdnBaseUrl}/${finalUrl}`

        const bodyImageId = generateBodyImageId()

        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: 'image',
              attrs: {
                src: fileUrl,
                alt: 'Image is provided with no description.',
                style: 'width: 320px; height: auto; cursor: pointer;',
                class: 'tiptap-responsive-img',
                'data-body-image-id': bodyImageId,
              },
            },
            {
              type: 'paragraph',
              content: [],
            },
          ])
          .run()

        if (hancleImageChange) {
          hancleImageChange({
            path: imagePath,
            name: file.name,
          })
        }

        if (onBodyImageAdded) {
          onBodyImageAdded({
            bodyImageId,
            url: finalUrl,
            name: file.name,
            size: file.size,
          })
        }

        onEditorLoading(false)
        if (onUploaded) onUploaded()
        return fileUrl
      } catch (error) {
        onEditorLoading(false)
        setEditorErrorMessage(
          formatMessage({
            id: 'createNotification.media.multipleImage.upload.failed',
          }),
        )
        if (onUploaded) onUploaded()
      }
    },

    [
      editor,
      onUploading,
      onUploaded,
      hancleImageChange,
      place,
      onBodyImageAdded,
    ],
  )

  const uploadExternalImages = useCallback(
    async (imageUrls) => {
      if (!imageUrls || imageUrls.length === 0) return []

      try {
        if (onUploading) onUploading()
        onEditorLoading(true)

        const baseUrl = apiConfigs.apiURL
        const uploadUrl = `${baseUrl}/places/${place.id}/upload-images-external`

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ imageUrls }),
          credentials: 'include',
        })

        if (!response.ok) {
          onEditorLoading(false)
          setEditorErrorMessage(
            formatMessage({
              id: 'createNotification.media.multipleImage.upload.failed',
            }),
          )
          throw new Error('Upload failed')
        }

        const results = await response.json()

        onEditorLoading(false)
        if (onUploaded) onUploaded()

        results.map((result, index) => {
          const imagePath = result.url
          if (!imagePath) return
          if (hancleImageChange) {
            hancleImageChange({
              path: imagePath,
              name: `paste-image-${index + 1}`,
            })
          }
        })

        return results // Array of {success, originalUrl, cdnUrl, url}
      } catch (error) {
        onEditorLoading(false)
        setEditorErrorMessage(
          formatMessage({
            id: 'createNotification.media.multipleImage.upload.failed',
          }),
        )
        if (onUploaded) onUploaded()

        // Return fallback response
        return imageUrls.map((url) => ({
          success: false,
          originalUrl: url,
          cdnUrl: url,
          url: null,
        }))
      }
    },
    [editor, onUploading, onUploaded, hancleImageChange, place],
  )

  const uploadFile = useCallback(
    async (file) => {
      if (!file || !editor) return

      const MAX_FILE_SIZE = 50 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        setEditorErrorMessage(
          formatMessage(
            { id: 'createNotification.media.maximumSize' },
            { limitSizeMB: 50 },
          ),
        )
        if (onUploaded) onUploaded()
        return
      }

      try {
        if (onUploading) onUploading()

        onEditorLoading(true)
        const baseUrl = apiConfigs.apiURL
        const uploadUrl = `${baseUrl}/places/${place.id}/upload`
        const base64 = await fileToBase64(file)

        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ file: base64 }),
          credentials: 'include',
        })

        if (!response.ok) {
          onEditorLoading(false)
          setEditorErrorMessage(
            formatMessage({
              id: 'createNotification.media.multipleImage.upload.failed',
            }),
          )
          throw new Error('Upload failed')
        }

        const result = await response.json()

        // Use the direct link from ImageKit (like videos and images)
        const finalUrl = result.url
        const cdnBaseUrl = apiConfigs.cndUrl
        const fileUrl = `${cdnBaseUrl}/${finalUrl}`

        if (!finalUrl) {
          onEditorLoading(false)
          setEditorErrorMessage('No link found in response:', result)
          if (onUploaded) onUploaded()
          return
        }

        // Insert file link into editor
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: file.name, // Shows "Book1.csv"
            marks: [
              {
                type: 'link',
                attrs: {
                  href: fileUrl, // Uses the ImageKit CDN URL
                  target: '_blank',
                  download: file.name,
                },
              },
            ],
          })
          .insertContent(' ')
          .run()
        onEditorLoading(false)
        if (onUploaded) onUploaded()
      } catch (error) {
        onEditorLoading(false)
        setEditorErrorMessage(
          formatMessage({
            id: 'createNotification.media.multipleImage.upload.failed',
          }),
        )
        if (onUploaded) onUploaded()
      }
    },
    [editor, onUploading, onUploaded, place],
  )

  const uploadVideo = useCallback(
    async (file) => {
      if (!file || !editor) return

      const MAX_FILE_SIZE = 50 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        setEditorErrorMessage(
          formatMessage(
            { id: 'createNotification.media.maximumSize' },
            { limitSizeMB: 50 },
          ),
        )
        if (onUploaded) onUploaded()
        return
      }

      if (!file.type.startsWith('video/')) {
        setEditorErrorMessage(
          formatMessage({ id: 'error.400.fileTypeInvalid' }),
        )
        if (onUploaded) onUploaded()
        return
      }

      try {
        if (onUploading) onUploading()
        onEditorLoading(true)
        const apiBaseUrl = apiConfigs.apiURL
        const uploadUrl = `${apiBaseUrl}/places/${place.id}/upload`
        const base64 = await fileToBase64(file)

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ file: base64 }),
          credentials: 'include',
        })

        if (!response.ok) {
          onEditorLoading(false)
          setEditorErrorMessage(
            formatMessage({
              id: 'createNotification.media.multipleImage.upload.failed',
            }),
          )
          throw new Error('Upload failed')
        }

        const result = await response.json()
        const videoPath = result.url

        if (!videoPath) {
          onEditorLoading(false)
          setEditorErrorMessage('No path found in response:', result)
          if (onUploaded) onUploaded()
          return
        }

        // Use CDN for video delivery (without image transformations)
        const cdnBaseUrl = apiConfigs.cndUrl
        const fileUrl = `${cdnBaseUrl}/${videoPath}`

        // Insert video element
        editor
          .chain()
          .focus()
          .setVideo({
            src: fileUrl,
            controls: true,
            width: 480, // Updated default width
            height: 270,
          })
          .run()
        onEditorLoading(false)
        if (onUploaded) onUploaded()
      } catch (error) {
        onEditorLoading(false)
        setEditorErrorMessage(
          formatMessage({
            id: 'createNotification.media.multipleImage.upload.failed',
          }),
        )
        if (onUploaded) onUploaded()
      }
    },
    [editor, onUploading, onUploaded, place],
  )

  const handleImageUpload = useCallback(
    async (e) => {
      const file = e.target.files[0]
      if (file) {
        await uploadImage(file)
      }
      e.target.value = ''
    },
    [uploadImage],
  )

  const handleFileUpload = useCallback(
    async (e) => {
      const file = e.target.files[0]
      if (file) {
        await uploadFile(file)
      }
      e.target.value = ''
    },
    [uploadFile],
  )

  const handleVideoUpload = useCallback(
    async (e) => {
      const file = e.target.files[0]
      if (file) {
        await uploadVideo(file)
      }
      e.target.value = ''
    },
    [uploadVideo],
  )

  const handleLinkClick = useCallback(() => {
    if (!editor) return

    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to)
    const previousUrl = editor.getAttributes('link').href

    const promptUrl = window.prompt('Enter URL:', previousUrl || 'https://')
    if (promptUrl === null) return

    const finalUrl = promptUrl.trim()
    if (!finalUrl) {
      editor.chain().focus().unsetLink().run()
      return
    }

    const normalizedUrl = finalUrl.startsWith('http')
      ? finalUrl
      : `https://${finalUrl}`

    const chain = editor.chain().focus()

    if (!selectedText.trim()) {
      // Insert link text and mark
      chain
        .insertContent({
          type: 'text',
          text: normalizedUrl,
          marks: [
            {
              type: 'link',
              attrs: {
                href: normalizedUrl,
                target: '_blank',
              },
            },
          ],
        })
        .insertContent('') // Break the link
        .run()
    } else {
      // Apply link to selected text
      chain
        .setLink({ href: normalizedUrl, target: '_blank' })
        .setTextSelection(to)
        .insertContent(' ')
        .run()
    }
  }, [editor])

  const handleInsertTable = useCallback(() => {
    if (!editor) return

    editor
      .chain()
      .focus()
      .insertTable({
        rows: 3,
        cols: 3,
        withHeaderRow: true,
      })
      .run()
  }, [editor])

  const MAX_COLUMNS = 10

  const handleAddColumnAfter = useCallback(() => {
    if (!editor) return

    const { state } = editor
    const $from = state.selection.$from

    let tableNode = null

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth)
      if (node.type.name === 'table') {
        tableNode = node
        break
      }
    }

    if (!tableNode) return

    const currentColumnCount = tableNode.firstChild?.childCount || 0

    if (currentColumnCount >= MAX_COLUMNS) {
      return
    }

    editor.chain().focus().addColumnAfter().run()
  }, [editor])

  return {
    exec,
    handleColorChange,
    handleFontFamily,
    handleFontSize,
    handleImageUpload,
    handleLinkClick,
    handleInsertTable,
    handleAddColumnAfter,
    uploadImage,
    uploadExternalImages,
    uploadFile,
    uploadVideo,
    handleFileUpload,
    handleVideoUpload,
  }
}

export const ImageWithResize = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: 'width: 320px; height: auto; cursor: pointer;',
        parseHTML: (element) => element.getAttribute('style') || '',
        renderHTML: (attributes) => ({ style: attributes.style }),
      },
      'data-body-image-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-body-image-id'),
        renderHTML: (attributes) => {
          if (!attributes['data-body-image-id']) return {}
          return { 'data-body-image-id': attributes['data-body-image-id'] }
        },
      },
    }
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { view } = editor
      const { style, src, alt } = node.attrs

      // Create DOM structure
      const $container = document.createElement('div')
      const $img = document.createElement('img')
      $container.setAttribute(
        'style',
        `display: inline-block; position: relative; max-width: 100%; ${style}`,
      )
      $img.setAttribute('src', src)
      if (alt) $img.setAttribute('alt', alt)
      $img.setAttribute('style', `${style} max-width: 100%; height: auto;`)
      $container.appendChild($img)

      // Get the editor container width for constraints
      const getMaxWidth = () => {
        const editorElement = view.dom.closest('.ProseMirror') || view.dom
        const containerWidth = editorElement.offsetWidth
        const padding = 80 // Account for editor padding
        return Math.max(200, containerWidth - padding)
      }

      // Resize handles
      const isMobile = document.documentElement.clientWidth < 768
      const dotSize = isMobile ? 16 : 9
      const dotPosition = isMobile ? '-8px' : '-4px'
      const dotsPosition = [
        `top: ${dotPosition}; left: ${dotPosition}; cursor: nwse-resize;`,
        `top: ${dotPosition}; right: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; left: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; right: ${dotPosition}; cursor: nwse-resize;`,
      ]

      let isResizing = false
      let startX, startWidth

      const dispatchNodeView = () => {
        if (typeof getPos === 'function') {
          view.dispatch(
            view.state.tr.setNodeMarkup(getPos(), null, {
              ...node.attrs,
              style: $img.getAttribute('style'),
            }),
          )
        }
      }

      $container.addEventListener('click', () => {
        // Remove existing dots
        while ($container.childElementCount > 1) {
          $container.removeChild($container.lastChild)
        }

        // Add resize handles
        Array.from({ length: 4 }, (_, index) => {
          const $dot = document.createElement('div')
          $dot.setAttribute(
            'style',
            `position: absolute; width: ${dotSize}px; height: ${dotSize}px; border: 1.5px solid #6C6C6C; border-radius: 50%; background: white; ${dotsPosition[index]}`,
          )

          $dot.addEventListener('mousedown', (e) => {
            e.preventDefault()
            isResizing = true
            startX = e.clientX
            startWidth = $container.offsetWidth

            const onMouseMove = (e) => {
              if (!isResizing) return
              const deltaX =
                index % 2 === 0 ? -(e.clientX - startX) : e.clientX - startX

              // Calculate new width with constraints
              const maxWidth = getMaxWidth()
              const newWidth = Math.max(
                50,
                Math.min(maxWidth, startWidth + deltaX),
              )

              $container.style.width = `${newWidth}px`
              $container.style.maxWidth = '100%'
              $img.style.width = `${newWidth}px`
              $img.style.maxWidth = '100%'
              $img.style.height = 'auto'
            }

            const onMouseUp = () => {
              isResizing = false
              dispatchNodeView()
              document.removeEventListener('mousemove', onMouseMove)
              document.removeEventListener('mouseup', onMouseUp)
            }

            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
          })

          $dot.addEventListener(
            'touchstart',
            (e) => {
              e.cancelable && e.preventDefault()
              isResizing = true
              startX = e.touches[0].clientX
              startWidth = $container.offsetWidth

              const onTouchMove = (e) => {
                if (!isResizing) return
                const deltaX =
                  index % 2 === 0
                    ? -(e.touches[0].clientX - startX)
                    : e.touches[0].clientX - startX

                // Calculate new width with constraints
                const maxWidth = getMaxWidth()
                const newWidth = Math.max(
                  50,
                  Math.min(maxWidth, startWidth + deltaX),
                )

                $container.style.width = `${newWidth}px`
                $container.style.maxWidth = '100%'
                $img.style.width = `${newWidth}px`
                $img.style.maxWidth = '100%'
                $img.style.height = 'auto'
              }

              const onTouchEnd = () => {
                isResizing = false
                dispatchNodeView()
                document.removeEventListener('touchmove', onTouchMove)
                document.removeEventListener('touchend', onTouchEnd)
              }

              document.addEventListener('touchmove', onTouchMove)
              document.addEventListener('touchend', onTouchEnd)
            },
            { passive: false },
          )

          $container.appendChild($dot)
        })

        $container.style.border = '1px dashed #6C6C6C'
      })

      // Remove handles when clicking outside
      document.addEventListener('click', (e) => {
        if (!$container.contains(e.target)) {
          $container.style.border = ''
          while ($container.childElementCount > 1) {
            $container.removeChild($container.lastChild)
          }
        }
      })

      return { dom: $container }
    }
  },
})

const LineHeight = Extension.create({
  name: 'lineHeight',

  addOptions() {
    return {
      types: LineHeightSupportedTags,
    }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {}
              return { style: `line-height: ${attributes.lineHeight}` }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ chain }) => {
          return this.options.types.reduce(
            (chainBuilder, type) =>
              chainBuilder.updateAttributes(type, { lineHeight }),
            chain(),
          )
        },
      unsetLineHeight:
        () =>
        ({ chain }) => {
          return this.options.types.reduce(
            (chainBuilder, type) =>
              chainBuilder.updateAttributes(type, { lineHeight: null }),
            chain(),
          )
        },
    }
  },
})

// try next iteration to make link in email
// 'a',
// {
//   href: videoUrl,
//   target: '_blank',
//   rel: 'noopener noreferrer',
//   class: `tiptap-youtube-video tiptap-youtube-${videoId}`,
//   style: `
//   display: block;
//   position: relative;
//   width: 100%;
//   padding-bottom: 56.25%;
//   height: 0;
//   overflow: hidden;
//   background: black ${thumbnailUrl ? `url(${thumbnailUrl}) center/cover no-repeat` : ''};
//   text-decoration: none;
// `,
// },
